import store from "../store";
import { BroadcastChannel } from "broadcast-channel";
import { LoggerFactory } from "../utilities/logger";
import { WindowCloser } from "./WindowCloser";
import { IConfiguration } from "../domain/configuration";

export interface ILaunchedInformation {
  by?: string;
  patientNo?: string;
  isForcibly?: boolean;
}

export interface IWindowQuitMessage extends ILaunchedInformation {
  shouldRegisterDocumentAsTemporary?: boolean;
}

export class WindowManager {
  static get instance(): WindowManager {
    return this._instance;
  }

  private static readonly _instance = new WindowManager();
  private static readonly _logger = LoggerFactory.getLogger();

// static初期化でBroadcastChannelを作るとIEで真っ白になる？(npm版も中でHTML5版を作るから同様になる？)
// 初回アクセス時に生成するようにしたら動いてるようなので、当面そっちで回避する.
  private _channel: BroadcastChannel;

  private get channel(): BroadcastChannel {
    if (!this._channel) {
      this._channel = this.createChannel();
    }
    return this._channel;
  }

  private createChannel(): BroadcastChannel {
    try {
      // HTML5標準のBroadcastChannelはIE/Safari非対応.非対応時はLocalStorageで代替されてるnpmモジュールを利用.
      return new BroadcastChannel("quit");
    } catch (e) {
      WindowManager._logger.error("BroadcastChannel生成できませんでした.");
      console.error(e);
      return null;
    }
  }

  private processChannel(process: (c: BroadcastChannel) => void): void {
    if (this.channel) {
      process(this.channel);
    } else {
      WindowManager._logger.warn("BroadcastChannelが生成できてないため処理を行いません.");
    }
  }

  initialize(launched: ILaunchedInformation, isBackground: boolean): void {
    if (isBackground) {
      WindowManager._logger.info("background mode detected.");
      this._shouldIgnoreQuitMessage = true;
    }
    if (this._launched) {
      WindowManager._logger.info("window manager is already initialized.");
    } else {
      WindowManager._logger.info("initialize window manager.");
      console.log(launched);
      this._launched = launched ? launched : {};  // 未指定の場合は空インスタンスを指定する.(undefinedが初期化未扱いなので.)
      this.processChannel(c => c.onmessage = m => this.QuitSelf(m));
    }
  }

  private _launched: ILaunchedInformation = undefined;
  private _shouldIgnoreQuitMessage: boolean = false;

  private QuitSelf(message: IWindowQuitMessage): void {
    WindowManager._logger.info("handle quit request.");
    console.log(message);
    if (!this._shouldIgnoreQuitMessage && this.shouldQuit(message)) {
      this.executeQuitAsync(message);
    }
  }

  private async executeQuitAsync(message: IWindowQuitMessage): Promise<void> {
    const isForcibly = message ? message.isForcibly : false;
    const shouldRegisterDocumentAsTemporary = message ? message.shouldRegisterDocumentAsTemporary : false;
    if (shouldRegisterDocumentAsTemporary) {
      try {
        await WindowCloser.registerDocumentAsTemporaryBeforeCloseHandler(isForcibly);
      } catch (e) {
        WindowManager._logger.error("終了連携での一時保存処理中にエラーが発生しました.");
        console.error(e);
      }
    }
    if (isForcibly || shouldRegisterDocumentAsTemporary || await WindowCloser.closeConfirmation()) {
      try {
        await WindowCloser.closeHandler();
      } finally {
        WindowCloser.closeSelf();
      }
    }
  }

  private shouldQuit(message: IWindowQuitMessage): boolean {
    if (this._launched && this._launched.by) {
      const configuration = store.getters.configuration as IConfiguration;
      if (configuration && configuration.system.window.manager.shouldIgnoreQuitWhenLaunchedByHasValueAndMessageValueIsEmpty && (!message || !message.by)) {
        WindowManager._logger.info("launchedByが指定されていないため、終了連携を無視します。");
        return false;
      }
    }
    return !message
        || !message.by
        || (this._launched && (message.by === this._launched.by) && (!message.patientNo || (message.patientNo === this._launched.patientNo)));
  }

  quit(message: IWindowQuitMessage): void {
    WindowManager._logger.info("request quit.");
    console.log(message);
    this.processChannel(c => c.postMessage(message));
  }
}
